import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Dashboard/Header/Header';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import {
  ArrowLeft,
  CreditCard,
  Building2,
  FileText,
  Info,
  Calendar
} from 'lucide-react';
import styles from './AddPaymentReceived.module.css';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';

const AddPaymentReceived = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({
    projectName: 'Commercial Tower A',
    workOrderNumber: 'WO-2023-0892 - Electrical Wiring',
    paymentDate: '2023-11-20',
    amountReceived: '250000',
    paymentType: 'Cheque / DD',
    bankName: 'State Bank of India',
    chequeNumber: '441092',
    chequeDate: '2023-11-18',
    remarks: ''
  });

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className={styles['add-payment-container']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['header-section']}>
            <h1>Add Payment Received</h1>
            <p>Log a new financial transaction for this work order.</p>
          </div>

          <div className={styles['form-layout']}>
            <div className={styles['form-sections']}>
              <section className={styles['form-card']}>
                <div className={styles['card-title']}>
                  <Building2 size={20} />
                  <span>Project Selection</span>
                </div>
                <div className={styles['input-grid']}>
                  <div className={styles['input-group']}>
                    <label>Project Name</label>
                    <select
                      name="projectName"
                      value={formData.projectName}
                      onChange={handleInputChange}
                    >
                      <option>Commercial Tower A</option>
                      <option>Skyline Residency</option>
                    </select>
                  </div>
                  <div className={styles['input-group']}>
                    <label>Work Order Number</label>
                    <select
                      name="workOrderNumber"
                      value={formData.workOrderNumber}
                      onChange={handleInputChange}
                    >
                      <option>WO-2023-0892 - Electrical Wiring</option>
                      <option>WO-2023-005 - Plumbing Work</option>
                    </select>
                  </div>
                </div>
              </section>

              <section className={styles['form-card']}>
                <div className={styles['card-title']}>
                  <CreditCard size={20} />
                  <span>Payment Details</span>
                </div>
                <div className={styles['input-grid']}>
                  <div className={styles['input-group']}>
                    <label>Payment Date</label>
                    <CustomDatePicker
                      selected={formData.paymentDate}
                      onChange={(date) => setFormData(prev => ({ ...prev, paymentDate: date ? date.toISOString().split('T')[0] : '' }))}
                      placeholderText="Select payment date"
                      minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                    />
                  </div>
                  <div className={styles['input-group']}>
                    <label>Amount Received (₹)</label>
                    <input
                      type="number"
                      name="amountReceived"
                      placeholder="Enter amount"
                      value={formData.amountReceived}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className={styles['input-group']}>
                    <label>Payment Type</label>
                    <select
                      name="paymentType"
                      value={formData.paymentType}
                      onChange={handleInputChange}
                    >
                      <option>Cash</option>
                      <option>Cheque / DD</option>
                      <option>Online Transfer</option>
                    </select>
                  </div>
                  <div className={styles['input-group']}>
                    <label>Bank Name</label>
                    <input
                      type="text"
                      name="bankName"
                      placeholder="Enter bank name"
                      value={formData.bankName}
                      onChange={handleInputChange}
                    />
                  </div>

                  {formData.paymentType === 'Cheque / DD' && (
                    <div className={styles['cheque-details-highlight']}>
                      <div className={styles['input-group']}>
                        <label>Cheque Number</label>
                        <input
                          type="text"
                          name="chequeNumber"
                          placeholder="Enter cheque number"
                          value={formData.chequeNumber}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className={styles['input-group']}>
                        <label>Cheque Date</label>
                        <CustomDatePicker
                          selected={formData.chequeDate}
                          onChange={(date) => setFormData(prev => ({ ...prev, chequeDate: date ? date.toISOString().split('T')[0] : '' }))}
                          placeholderText="Select cheque date"
                          minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </section>

              <section className={styles['form-card']}>
                <div className={styles['card-title']}>
                  <FileText size={20} />
                  <span>Remarks</span>
                </div>
                <div className={[styles['input-group'], styles['full-width']].join(' ')}>
                  <textarea
                    name="remarks"
                    placeholder="Enter any internal notes or payment references..."
                    value={formData.remarks}
                    onChange={handleInputChange}
                  />
                </div>
              </section>
            </div>

            <div className={styles['summary-sidecar']}>
              <div className={styles['summary-card']}>
                <div className={styles['summary-header']}>
                  <h2>Financial Summary</h2>
                </div>
                <div className={styles['summary-body']}>
                  <div className={styles['summary-item']}>
                    <span className={styles['label']}>Work Order Value</span>
                    <span className={styles['value']}>₹ 12,50,000.00</span>
                  </div>
                  <div className={styles['summary-item']}>
                    <span className={styles['label']}>Total Received</span>
                    <span className={[styles['value'], styles['received']].join(' ')}>₹ 8,00,000.00</span>
                  </div>

                  <div className={styles['outstanding-box']}>
                    <span className={styles['label']}>Outstanding Amount</span>
                    <span className={styles['value']}>₹ 4,50,000.00</span>
                  </div>
                </div>
                <div className={styles['summary-footer']}>
                  <Info size={18} />
                  <p>Recording this payment will update the ledger and project completion status.</p>
                </div>
              </div>
            </div>
          </div>
          <div className={styles['form-actions']}>
            <button className={styles['draft-btn']}>Save Draft</button>
            <button className={styles['confirm-btn']}>
              <span>Confirm Payment</span>
            </button>
            <button 
              className={styles['cancel-btn']} 
              onClick={() => navigate('/payment/payments-received')}
            >
              Cancel
            </button>
          </div>
        </main>


      </div>
    </div>
  );
};

export default AddPaymentReceived;
